import AppStore from "../../../app.store";
import axios from "axios";
import {api} from "../../../api/endpoints";
import {OwnerSettingsModel} from "./owner.settings.model";
import {makeAutoObservable, runInAction} from "mobx";
import {toast} from "react-toastify";
import {
    DEFAULT_DUPLICATE_CHECK_FIELDS,
    normalizeDuplicateCheckFields
} from "../../clients/clients.duplicates/duplicate.fields";
import {DEFAULT_TIME_ZONE, getBrowserTimeZone} from "../../../utils/timezone";

import {getRequestErrorReason} from '../../../utils/requestError';
import {normalizeProductPickerModes} from '../../../components/ProductPicker';

interface PaymentMethodWithStatus {
    id: number;
    code: string;
    name: string;
    description: string;
    enabled: boolean;
    owner_payment_method_id?: number;
}

const ownerInitialValue: OwnerSettingsModel = {
    installment_discount_from_prepayment: 1,
    installments: false,
    multiline_product_in_sales: false,
    prepayment_from_total_amount: false,
    installment_penalty_daily_percent: 1,
    installment_penalty_grace_days: 0,
    selling_currency: "",
    sms_installment_approval: false,
    sms_installment_complete: false,
    sms_installment_overdue: false,
    sms_module: false,
    sms_payback_warning: false,
    active: false,
    created_at: '',
    employee_bonus: false,
    id: 0,
    name: '',
    phone: '',
    check_password: true,
    check_otp: false,
    reference: 0,
    transaction_approval: false,
    allow_sale_on_shortage: false,
    updated_at: '',
    cheque_double_record: false,
    product_commissions: false,
    product_reverse_commission: false,
    changeable_total_amount: false,
    working_hours: false,
    select_employee: false,
    ask_for_paying_client_info: false,
    paying_client_phone: false,
    paying_client_full_name: false,
    paying_client_date_of_birth: false,
    paying_client_passport_number: false,
    paying_client_passport_issuing_authority: false,
    paying_client_passport_date_of_issue: false,
    paying_client_passport_date_of_expiry: false,
    duplicate_trn_timeout_seconds: 0,
    refund_period_timeout_days: 0,
    product_min_max: false,
    product_dimensions: false,
    auto_transfer_sales: false,
    telegram_notification: false,
    chat_id: 0,
    multicurrency: false,
    time_zone: DEFAULT_TIME_ZONE,
    time_zone_auto: true,
    main_currency: '',
    income_tax_rate: 0,
    social_tax_rate: 0,
    cashback: false,
    cashback_general_enabled: false,
    cashback_general_percentage: 0,
    cashback_base: 'paid_amount',
    cashback_product_base: 'paid_amount',
    cache_last_records_limit: 5,
    client_search_full_name: true,
    client_search_phone: true,
    client_search_inn: true,
    client_search_id: true,
    client_search_card_pan: false,
    product_picker_mode: 'quick_search',
    product_picker_modes: {
        orders: 'quick_search',
        operations: 'quick_search',
        purchases: 'quick_search',
    },
    duplicate_check_enabled: false,
    duplicate_check_threshold: 70,
    duplicate_check_on_create: true,
    duplicate_check_in_list: true,
    duplicate_check_fields: [...DEFAULT_DUPLICATE_CHECK_FIELDS],
    duplicate_check_worker_enabled: false,
    duplicate_check_worker_interval_hours: 24,
    duplicate_check_worker_last_run_at: null,
    duplicate_check_worker_last_group_count: 0,
    duplicate_check_worker_last_pair_count: 0,
}

const loadingInitialState: boolean = false;

const serializeOwnerSettings = (settings: OwnerSettingsModel) => {
    const normalizedModes = normalizeProductPickerModes(
        settings.product_picker_modes,
        settings.product_picker_mode,
    );

    return JSON.stringify({
        ...settings,
        duplicate_check_fields: normalizeDuplicateCheckFields(settings.duplicate_check_fields),
        duplicate_check_threshold: Number(settings.duplicate_check_threshold || 0),
        duplicate_check_worker_interval_hours: Number(settings.duplicate_check_worker_interval_hours || 0),
        cashback_general_percentage: Number(settings.cashback_general_percentage || 0),
        cashback_base: settings.cashback_base || 'paid_amount',
        cashback_product_base: settings.cashback_product_base || 'paid_amount',
        installment_penalty_daily_percent: Number(settings.installment_penalty_daily_percent ?? 1),
        installment_penalty_grace_days: Number(settings.installment_penalty_grace_days || 0),
        product_picker_modes: normalizedModes,
        product_picker_mode: normalizedModes.orders,
        time_zone: settings.time_zone_auto
            ? getBrowserTimeZone()
            : (settings.time_zone || DEFAULT_TIME_ZONE),
    });
};

class OwnerSettings {
    public ownerSettings = ownerInitialValue;
    public ownerSettingsLoading = loadingInitialState;
    public ownerSettingsUpdateLoading = loadingInitialState;
    public paymentMethods: PaymentMethodWithStatus[] = [];
    public paymentMethodsLoading = loadingInitialState;
    private savedSettingsSnapshot: string = '';
    message: string = '';
    error: string = '';

    constructor() {
        makeAutoObservable(this);
    }

    public get isDirty() {
        if (!this.savedSettingsSnapshot) {
            return false;
        }
        return serializeOwnerSettings(this.ownerSettings) !== this.savedSettingsSnapshot;
    }

    private captureSavedSnapshot = () => {
        this.savedSettingsSnapshot = serializeOwnerSettings(this.ownerSettings);
    }

    public ownerSettingsPageInit = async (signal: AbortSignal) => {
        await this.ownerSettingsReq(signal);
    }

    setOwnerSettings(data: any) {
        const {owner} = data;
        owner.duplicate_check_fields = normalizeDuplicateCheckFields(owner?.duplicate_check_fields);
        owner.time_zone = owner?.time_zone || getBrowserTimeZone();
        owner.time_zone_auto = owner?.time_zone_auto ?? true;
        owner.cashback_general_enabled = owner?.cashback_general_enabled ?? false;
        owner.cashback_general_percentage = owner?.cashback_general_percentage ?? 0;
        owner.cashback_base = owner?.cashback_base || 'paid_amount';
        owner.cashback_product_base = owner?.cashback_product_base || owner?.cashback_base || 'paid_amount';
        owner.check_password = owner?.check_password ?? true;
        owner.check_otp = owner?.check_otp ?? false;
        owner.product_picker_mode = owner?.product_picker_mode || 'quick_search';
        owner.product_picker_modes = normalizeProductPickerModes(
            owner?.product_picker_modes,
            owner.product_picker_mode,
        );
        owner.auto_transfer_sales = owner?.auto_transfer_sales ?? false;
        owner.telegram_notification = owner?.telegram_notification ?? false;
        owner.chat_id = Number(owner?.chat_id) || 0;
        owner.installments = owner?.installments ?? false;
        owner.prepayment_from_total_amount = owner?.prepayment_from_total_amount ?? false;
        owner.multiline_product_in_sales = owner?.multiline_product_in_sales ?? false;
        owner.changeable_total_amount = owner?.changeable_total_amount ?? false;
        owner.select_employee = owner?.select_employee ?? false;
        owner.installment_discount_from_prepayment = Number(owner?.installment_discount_from_prepayment) || 1;
        owner.installment_penalty_daily_percent = owner?.installment_penalty_daily_percent ?? 1;
        owner.installment_penalty_grace_days = owner?.installment_penalty_grace_days ?? 0;
        if (!owner.check_password && !owner.check_otp) {
            owner.check_password = true;
        }
        this.ownerSettings = owner;
        this.captureSavedSnapshot();
    }

    setLoading(loading: boolean) {
        this.ownerSettingsLoading = loading;
    }

    setUpdateLoading(loading: boolean) {
        this.ownerSettingsUpdateLoading = loading;
    }

    public setOwnerSettingsForm = (form: OwnerSettingsModel) => {
        this.ownerSettings = form;
    }

    public ownerSettingsReq = async (signal?: any) => {
        this.setLoading(true);

        try {
            const {getToken} = AppStore;

            const headers = {
                Authorization: 'Bearer ' + getToken()!,
            }

            const response = await axios.get(api.owner, {headers, signal});

            this.setOwnerSettings(response?.data);
        } catch (err) {
            console.error(err);
        } finally {
            this.setLoading(false);
        }
    }

    public changeSettingsReq = async () => {
        this.setUpdateLoading(true);

        const {getToken} = AppStore;
        const payload = {
            ...this.ownerSettings,
            time_zone: this.ownerSettings.time_zone_auto
                ? getBrowserTimeZone()
                : (this.ownerSettings.time_zone || DEFAULT_TIME_ZONE),
            duplicate_check_fields: normalizeDuplicateCheckFields(this.ownerSettings.duplicate_check_fields),
            duplicate_check_threshold: Number(this.ownerSettings.duplicate_check_threshold || 0),
            duplicate_check_worker_interval_hours: Number(this.ownerSettings.duplicate_check_worker_interval_hours || 0),
            cashback_general_percentage: Number(this.ownerSettings.cashback_general_percentage || 0),
            cashback_base: this.ownerSettings.cashback_base || 'paid_amount',
            cashback_product_base: this.ownerSettings.cashback_product_base || 'paid_amount',
            installment_penalty_daily_percent: Number(this.ownerSettings.installment_penalty_daily_percent ?? 1),
            installment_penalty_grace_days: Number(this.ownerSettings.installment_penalty_grace_days || 0),
            product_picker_modes: normalizeProductPickerModes(
                this.ownerSettings.product_picker_modes,
                this.ownerSettings.product_picker_mode,
            ),
            product_picker_mode: normalizeProductPickerModes(
                this.ownerSettings.product_picker_modes,
                this.ownerSettings.product_picker_mode,
            ).orders,
        };

        const headers = {
            Authorization: 'Bearer ' + getToken()!,
        }

        try {
            const response = await axios.put(api.owner, payload, {headers});
            this.message = response?.data.message;
            toast.success(this.message);
            // Keep local form in sync with normalized payload values
            runInAction(() => {
                this.ownerSettings = {
                    ...this.ownerSettings,
                    ...payload,
                };
                this.captureSavedSnapshot();
            });
            return response;
        } catch (e: any) {
            const reason = getRequestErrorReason(e);
            if (!reason) return;
            this.error = reason;
            toast.error(this.error);
        } finally {
            this.setUpdateLoading(false);
        }
    }

    public getPaymentMethodsWithStatus = async (signal?: AbortSignal) => {
        this.paymentMethodsLoading = true;

        try {
            const {getToken} = AppStore;
            const headers = {
                Authorization: 'Bearer ' + getToken()!,
            };

            const response = await axios.get(`${api.payment_methods}/with_status`, {headers, signal});
            runInAction(() => {
                this.paymentMethods = response?.data || [];
            });
        } catch (err: any) {
            console.error('Error fetching payment methods:', err);
            if (err?.response?.data?.reason) {
                const reason = getRequestErrorReason(err);
                if (reason) toast.error(reason);
            }
        } finally {
            this.paymentMethodsLoading = false;
        }
    }

    public togglePaymentMethod = async (paymentMethodId: number, enabled: boolean) => {
        if (!paymentMethodId || isNaN(paymentMethodId)) {
            toast.error('Ошибка: неверный ID способа оплаты');
            return;
        }

        // Проверяем, что не отключаем последний активный способ оплаты
        if (!enabled) {
            const enabledCount = this.paymentMethods.filter(pm => pm.enabled).length;
            if (enabledCount <= 1) {
                toast.error('Нельзя отключить последний активный способ оплаты. Должен остаться хотя бы один активный способ.');
                return;
            }
        }

        try {
            const {getToken} = AppStore;
            const headers = {
                Authorization: 'Bearer ' + getToken()!,
                'Content-Type': 'application/json',
            };

            // Явно создаем объект с правильными типами
            const requestData = {
                payment_method_id: Number(paymentMethodId),
                enabled: enabled
            };

            const response = await axios.post(
                `${api.payment_methods}/toggle`,
                requestData,
                {
                    headers
                }
            );

            // Обновляем локальное состояние
            runInAction(() => {
                const method = this.paymentMethods.find(pm => pm.id === paymentMethodId);
                if (method) {
                    method.enabled = enabled;
                }
            });

            toast.success(response?.data?.message || 'Способ оплаты обновлен');
            return response;
        } catch (e: any) {
            const errorMessage = e?.response?.data?.reason || 'Ошибка при обновлении способа оплаты';
            toast.error(errorMessage);
            throw e;
        }
    }
}

export default new OwnerSettings();
